import { Component, DestroyRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { GarageContract } from '../../../shared/models/contract';
import { ActivatedRoute } from '@angular/router';
import { TypedRoute } from '../../../shared/utils/router.utils';
import { CoreData, DashboardResolve } from '../../../shared/models/routeTyping';
import { ArivoDialogComponent } from '../../../shared/components/ui-components/arivo-dialog/arivo-dialog.component';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CoreDataService } from '../../../core/services/core-data.service';
import { formatAddress, getContractPriceAdjustmentIncreaseString } from '../../../shared/utils/common.utils';
import { FeatureFlagService } from '../../../core/services/feature-flag.service';
import { LocalizedCurrencyPipe } from '../../../core/pipes/localized-currency.pipe';
import { TranslateService } from '@ngx-translate/core';
import { ProviderLinkOptions } from '../../../shared/models/providerSettings';
import { ProviderSettingsService } from '../../../core/services/provider-settings.service';

@Component({
  selector: 'app-dashboard-contracts',
  templateUrl: './dashboard-contracts.component.html',
  styleUrl: './dashboard-contracts.component.scss',
})
export class DashboardContractsComponent implements OnInit {
  @ViewChild('qrDialog') qrDialog!: ArivoDialogComponent;
  private destroyRef = inject(DestroyRef);
  qrCode: null | string = null;
  hasPaymentMethod = false;
  creatingContractEnabled!: boolean;
  contractQrCodeEnabled!: boolean;
  qrImageError = false;

  contracts: GarageContract[] = [];
  providerLinks!: ProviderLinkOptions;

  constructor(
    @Inject(ActivatedRoute) private route: TypedRoute<DashboardResolve>,
    private coreDataService: CoreDataService,
    private http: HttpClient,
    private featureFlagService: FeatureFlagService,
    private localizedCurrencyPipe: LocalizedCurrencyPipe,
    private translateService: TranslateService,
    private providerSettingsService: ProviderSettingsService,
  ) {}

  ngOnInit(): void {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.contracts = this.sortContracts(data.dashboardApiResponse.garage_contracts);
    });
    this.coreDataService.coreData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((coreData: CoreData) => {
      this.hasPaymentMethod =
        coreData.signup_status.has_payment_method || this.featureFlagService.getFeatureFlag('dev_payment_method_optional');
    });
    this.providerSettingsService.providerSettings.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.providerLinks = this.providerSettingsService.getLinks();
    });
    this.creatingContractEnabled = this.featureFlagService.getFeatureFlag('enable_contract_creation_dashboard');
    this.contractQrCodeEnabled = this.featureFlagService.getFeatureFlag('enable_contract_qr_code');
  }

  sortContracts(contracts: GarageContract[]): GarageContract[] {
    return contracts.sort((a, b) => {
      const aHasNoLicensePlates = a.license_plates.length <= 0;
      const bHasNoLicensePlates = b.license_plates.length <= 0;
      const aIsActive = this.isContractActive(a);
      const bIsActive = this.isContractActive(b);

      if (aIsActive && bIsActive) {
        // Active contracts: prioritize those without license plates
        return Number(bHasNoLicensePlates) - Number(aHasNoLicensePlates);
      } else if (aIsActive !== bIsActive) {
        // Active contracts come before inactive contracts
        return Number(bIsActive) - Number(aIsActive);
      } else {
        // Both are inactive: no specific order
        return 0;
      }
    });
  }

  isRpcFallbackGarage(contract: GarageContract): boolean {
    if (!contract.garage) {
      return false;
    }
    return (
      !!contract.garage.name && !contract.garage.city && !contract.garage.street && !contract.garage.number && !contract.garage.zip_code
    );
  }

  openQrDialog(id: GarageContract['id']) {
    this.qrCode = this.contracts.find((contract) => contract.id === id)?.qr_src ?? null;
    if (this.qrCode) {
      this.qrDialog.open = true;
      this.qrImageError = false;
    }
  }

  handleQrCodeError() {
    this.qrImageError = true;
  }

  dateIsInFuture(date: Date): boolean {
    return date > new Date();
  }

  getPriceIncreaseString(garageContract: GarageContract): string | null {
    return getContractPriceAdjustmentIncreaseString(
      garageContract,
      'Dashboard.Contracts.PriceIncreaseInfo',
      this.translateService,
      this.localizedCurrencyPipe,
    );
  }

  get showVpi() {
    return this.featureFlagService.getFeatureFlag('enable_wipark_vpi');
  }

  isContractActive(contract: GarageContract): boolean {
    return contract.end ? new Date(contract.end) > new Date() : true;
  }

  protected readonly formatAddress = formatAddress;
}
